import { useCallback, useEffect, useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, Edit, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { useCategories, useDeleteCategory } from '@/hooks/useCategories';
import { useCategoryStore } from '@/stores/categoryStore';
import { CategoryWithRelations } from '@/features/categories/types';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { ErrorDisplay } from '@/components/common/ErrorBoundary';
import { formatCurrency } from '@/utils/currencyFormatter';
import { useProviderCurrency } from '@/stores/currencyStore';

interface CategoryListProps {
  providerId: number;
  versionId: number;
  isReadOnly?: boolean;
  onCreateCategory: () => void;
}

interface CategoryItemProps {
  category: CategoryWithRelations;
  level: number;
  selectedCategory: CategoryWithRelations | null;
  selectedCategories: number[];
  onSelect: (category: CategoryWithRelations) => void;
  onToggleSelect: (id: number) => void;
  isReadOnly: boolean;
}

function CategoryItem({
  category,
  level,
  selectedCategory,
  selectedCategories,
  onSelect,
  onToggleSelect,
  isReadOnly
}: CategoryItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = (category.children || []).length > 0;
  const isParent = !category.parent_id;
  const { openEditModal, openDeleteModal } = useCategoryStore();
  const currency = useProviderCurrency(category.provider_id);
  
  return (
    <div className="space-y-0.5">
      <div
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          selectedCategory?.id === category.id && "bg-accent text-accent-foreground"
        )}
        style={{ paddingLeft: `${level * 1.5 + 1}rem` }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="flex-shrink-0" style={{ width: '20px' }}>
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className="p-1 hover:bg-accent rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            )}
          </div>
          <Checkbox
            checked={selectedCategories.includes(category.id)}
            onCheckedChange={() => onToggleSelect(category.id)}
            aria-label={`Select ${category.name}`}
            className="flex-shrink-0"
          />
          <div 
            className="flex-1 min-w-0 cursor-pointer" 
            onClick={() => onSelect(category)}
          >
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">{category.name}</span>
              {category.price && (
                <Badge variant="outline">
                  {formatCurrency(category.price, currency)}
                </Badge>
              )}
              {category.is_addon && (
                <Badge variant="destructive" className="flex-shrink-0">
                  Add-on
                </Badge>
              )}
              <Badge 
                variant={isParent ? "secondary" : "outline"} 
                className="flex-shrink-0"
              >
                {isParent ? "P" : "S"}
              </Badge>
            </div>
            {category.description && (
              <p className="text-sm text-muted-foreground truncate">
                {category.description}
              </p>
            )}
          </div>
        </div>

        {!isReadOnly && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openEditModal(category);
              }}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openDeleteModal(category);
              }}
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="ml-4">
          {category.children?.map(child => (
            <CategoryItem
              key={child.id}
              category={child}
              level={level + 1}
              selectedCategory={selectedCategory}
              selectedCategories={selectedCategories}
              onSelect={onSelect}
              onToggleSelect={onToggleSelect}
              isReadOnly={isReadOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CategoryList({ providerId, versionId, onCreateCategory }: CategoryListProps) {
  const { data: categoriesData = [], isLoading, error } = useCategories(providerId, versionId);
  const deleteCategory = useDeleteCategory();
  
  const { 
    selectedCategory, 
    setSelectedCategory, 
    selectedCategories,
    toggleCategorySelection,
  } = useCategoryStore();

  // Select first category by default
  useEffect(() => {
    if (categoriesData?.length > 0 && !selectedCategory) {
      setSelectedCategory(categoriesData[0]);
    }
  }, [categoriesData, selectedCategory, setSelectedCategory]);

  const handleCategorySelect = useCallback((category: CategoryWithRelations) => {
    setSelectedCategory(category);
  }, [setSelectedCategory]);

  const rootCategories = useMemo(() => 
    categoriesData.filter((cat: CategoryWithRelations) => !cat.parent_id),
    [categoriesData]
  );

  const handleDeleteSelected = async (event: React.MouseEvent) => {
    event.preventDefault();
    if (!selectedCategories.length) return;

    try {
      await Promise.all(
        selectedCategories.map(categoryId => 
          deleteCategory.mutateAsync({
            categoryIds: [categoryId],
            providerId,
            versionId
          })
        )
      );
      toast.success("Categories deleted successfully");
    } catch (error) {
      toast.error("Failed to delete categories");
    }
  };

  if (error) return <ErrorDisplay error={error} />;

  // Instead of showing nothing, show empty state
  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Categories</h2>
          <Button 
            onClick={(e) => {
              e.preventDefault();
              onCreateCategory();
            }}
            size="sm"
            variant="default"
          >
            Add Category
          </Button>
        </div>
        <div className="flex items-center justify-center h-[200px]">
          <div className="text-muted-foreground text-sm">Loading categories...</div>
        </div>
      </div>
    );
  }

  // Show empty state with create button when no categories
  if (!categoriesData?.length) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Categories</h2>
          <Button 
            onClick={(e) => {
              e.preventDefault();
              onCreateCategory();
            }}
            size="sm"
            variant="default"
          >
            Add Category
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center h-[200px] gap-4 text-center">
          <p className="text-muted-foreground text-sm">No categories found</p>
          <Button 
            onClick={(e) => {
              e.preventDefault();
              onCreateCategory();
            }}
            variant="outline"
          >
            Create your first category
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Categories</h2>
        <Button 
          onClick={(e) => {
            e.preventDefault();
            onCreateCategory();
          }}
          size="sm"
          variant="default"
        >
          Add Category
        </Button>
      </div>

      {selectedCategories.length > 0 && (
        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
          <span className="text-sm font-medium">
            {selectedCategories.length} selected
          </span>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteSelected}
          >
            Delete Selected
          </Button>
        </div>
      )}

      <div className="space-y-0.5">
        {rootCategories.map((category: CategoryWithRelations) => (
          <CategoryItem
            key={category.id}
            category={category}
            level={0}
            selectedCategory={selectedCategory}
            selectedCategories={selectedCategories}
            onSelect={handleCategorySelect}
            onToggleSelect={toggleCategorySelection}
            isReadOnly={false}
          />
        ))}
      </div>
    </div>
  );
}

